import React, { ButtonHTMLAttributes, DOMAttributes, useState } from "react";
import classNames from "classnames";

type ButtonCommonProps = Partial<
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    keyof DOMAttributes<any> | "onBeforeToggle" | "onToggle"
  >
>;
type LinkCommonProps = Partial<
  Omit<
    React.LinkHTMLAttributes<Omit<HTMLLinkElement, "href">>,
    | "type"
    | "as"
    | "href"
    | keyof DOMAttributes<any>
    | "onBeforeToggle"
    | "onToggle"
  >
>;

interface CommonProps<T>
  extends ButtonCommonProps,
    LinkCommonProps,
    DOMAttributes<T> {}

type IconButtonProps<T = any> = CommonProps<T> & {
  children: React.ReactNode;
  component?: "button" | "link";
  className?: string;
  disabled?: boolean;
  href?: string;
  target?: string;
  label?: string | React.ReactElement | React.ReactNode;
  variant?: "square" | "rounded";
  labelPosition?: "start" | "end";
  color?: "black" | "white-cerise";
};

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      children,
      component = "button",
      labelPosition = "end",
      href,
      className,
      label,
      variant = "square",
      color = "black",
      ...props
    },
    ref
  ) => {
    const defaultStyles =
      "relative inline-flex items-center justify-center leading-[100%] duration-200 ease-in-out transition-[background-color,box-shadow,color,border-color]";
    const defaultAfterStyles =
      "after:pointer-events-none after:bg-black-rock after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 after:opacity-0 after:duration-200 after:ease-in-out after:transition-opacity after:content-['']";
    const focusCommonStyles =
      "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring focus-visible:ring-inset";
    const disabledStyles =
      "disabled:pointer-events-none disabled:text-mono-20 disabled:user-select-none";

    const commonStyles = classNames(
      defaultStyles,
      defaultAfterStyles,
      disabledStyles,
      focusCommonStyles
    );

    const variantStyles = {
      square: "rounded-none bg-transparent focus-visible:ring-black",
      rounded:
        "rounded-full after:rounded-full bg-mono-15 hover:after:opacity-[10%] active:after:opacity-[15%]",
    }[variant];

    const squareColorStyles = {
      "square-black":
        "text-black-rock hover:after:opacity-[10%] active:after:opacity-[15%] focus-visible:ring-black",
      "square-white-cerise":
        "text-white hover:after:opacity-0 active:after:opacity-0 hover:bg-cerise active:bg-cerise focus-visible:ring-white",
    }[`${variant}-${color}`];

    const sizeStyles = classNames({
      "w-[36px] h-[36px] p-[6px]": !label && variant === "square",
      "h-[36px] p-[6px] max-w-fit": label && variant === "square",
      "h-[40px] w-[40px]": variant === "rounded",
    });

    const componentProps = {
      className: classNames(
        commonStyles,
        variantStyles,
        squareColorStyles,
        sizeStyles,
        className,
        {
          "pointer-events-none": props.disabled,
        }
      ),
    };

    const getChildren = () => {
      return (
        <>
          {label && labelPosition === "start" && variant !== "rounded" && (
            <div className={"mr-2 font-bold text-[16px] leading-[130%]"}>
              {label}
            </div>
          )}
          <div
            className={
              "inline-flex h-[24px] w-[24px] items-center justify-center"
            }
          >
            {children}
          </div>
          {label && labelPosition === "end" && variant !== "rounded" && (
            <div className={"ml-2 font-bold text-[16px] leading-[130%]"}>
              {label}
            </div>
          )}
        </>
      );
    };

    if (component === "button") {
      return (
        <button ref={ref} {...componentProps} {...props}>
          {getChildren()}
        </button>
      );
    }

    if (!href) {
      return null;
    }

    return (
      <a
        ref={ref as React.LegacyRef<HTMLAnchorElement> | undefined}
        href={href}
        referrerPolicy={"strict-origin-when-cross-origin"}
        tabIndex={props.disabled ? -1 : undefined}
        {...componentProps}
        {...props}
      >
        {getChildren()}
      </a>
    );
  }
);

export interface RoundIconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The icon component to display
   */
  icon: React.ReactNode;
  /**
   * Visual style variant for round icon button.
   */
  variant?: "default" | "sifted-carousel";
}

/**
 * A round, icon-only button with a hover effect.
 */
const RoundIconButton: React.FC<RoundIconButtonProps> = ({
  icon,
  className,
  disabled,
  variant = "default",
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const defaultVariantClassName = classNames(
    "relative flex h-9 w-9 items-center justify-center rounded-3xl border-2 border-brand-neutral p-1",
    "transition-all duration-200",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-neutral focus-visible:ring-offset-2",
    {
      "bg-mono-10": isHovered && !disabled,
      "bg-white": !isHovered && !disabled,
      "cursor-not-allowed opacity-50": disabled,
    }
  );

  const siftedCarouselVariantClassName = classNames(
    "relative flex h-9 w-9 items-center justify-center rounded-full border-0 bg-sifted-20 p-1",
    "duration-200 ease-in-out transition-[background-color,box-shadow,color,border-color]",
    "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-inset",
    "after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 after:rounded-full after:bg-black-rock after:opacity-0 after:duration-200 after:ease-in-out after:transition-opacity after:content-['']",
    "hover:after:opacity-[10%] active:after:opacity-[15%]",
    "disabled:cursor-not-allowed disabled:opacity-30 disabled:after:opacity-0"
  );

  const baseClassName = classNames(
    variant === "sifted-carousel"
      ? siftedCarouselVariantClassName
      : defaultVariantClassName,
    className
  );

  return (
    <button
      type="button"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={baseClassName}
      disabled={disabled}
      {...props}
    >
      {icon}
    </button>
  );
};
export default IconButton;

export { RoundIconButton };
